import { api } from "./api";

const BASE = "/api/v1/integrations/johndeere";

export interface JohnDeereStatus {
  connected: boolean;
}

export interface JohnDeereOrganization {
  id: string;
  name: string;
  selfUri: string;
}

export interface JohnDeereFarm {
  id: string;
  name: string;
  selfUri: string;
}

export interface JohnDeereClient {
  id: string;
  name: string;
  selfUri: string;
}

export interface JohnDeereField {
  id: string;
  name: string;
  area?: number;
  selfUri: string;
}

export interface JohnDeereImportResult {
  imported: Array<{ id: string; name: string; externalId?: string }>;
  errors?: string[];
}

export interface JohnDeerePrescriptionUploadResult {
  termsLocation?: string;
}

/**
 * `url` when it is an https link on John Deere's own domain, else null.
 * connectionsUrl (sometimes Deere's own org link) and termsLocation (a Deere
 * response header) reach href / window.open from outside our code, and React
 * does not block a `javascript:` href.
 */
export const safeDeereUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    if (parsed.protocol !== "https:") return null;
    const deere = ["deere.com", "johndeere.com"].some((d) => host === d || host.endsWith(`.${d}`));
    return deere ? parsed.toString() : null;
  } catch {
    return null;
  }
};

/**
 * `silent`: this is a connection probe, called on menu open as well as from the
 * profile section. A failure means "not connected" and the UI renders that
 * state itself — a toast on top of it is just noise.
 */
export const getJohnDeereStatus = (): Promise<JohnDeereStatus> =>
  api.get<JohnDeereStatus>(`${BASE}/status`, undefined, { silent: true });

export const getJohnDeereConnectUrl = (): string => {
  const base = process.env.REACT_APP_API_URL ?? window.location.origin;
  return `${base}${BASE}/connect`;
};

export const disconnectJohnDeere = (): Promise<void> =>
  api.delete<void>(`${BASE}/disconnect`);

export interface JohnDeereOrganizationsResponse {
  organizations: JohnDeereOrganization[];
  connectionRequired?: boolean;
  connectionsUrl?: string;
}

export const getJohnDeereOrganizations = (): Promise<JohnDeereOrganizationsResponse> =>
  api.get<JohnDeereOrganizationsResponse>(`${BASE}/organizations`);

export const getJohnDeereFarms = (orgUri: string): Promise<{ farms: JohnDeereFarm[] }> =>
  api.get<{ farms: JohnDeereFarm[] }>(`${BASE}/farms`, { orgUri });

/** List clients (growers) for an organization */
export const getJohnDeereClients = (orgUri: string): Promise<{ clients: JohnDeereClient[] }> =>
  api.get<{ clients: JohnDeereClient[] }>(`${BASE}/clients`, { orgUri });

/**
 * One file in an organization's Operations Center file store.
 *
 * The store is a generic dump — setup files, boundary exports, prescriptions —
 * so the backend filters to the archive types our parser can read and we show
 * the name and date for the user to choose from.
 */
export interface JohnDeereFile {
  id: string;
  name: string;
  type: string | null;
  sizeBytes: number | null;
  createdTime: string | null;
}

/** List an org's importable files (zip / kml / kmz / shp). */
export const getJohnDeereFiles = (
  orgUri: string
): Promise<{ files: JohnDeereFile[]; totalUnfiltered: number }> =>
  api.get<{ files: JohnDeereFile[]; totalUnfiltered: number }>(`${BASE}/files`, { orgUri });

/**
 * Download one file's bytes. Returned as a Blob so the caller can wrap it in a
 * File and run it through the same parser a manual upload uses.
 */
export const downloadJohnDeereFile = (fileId: string): Promise<Blob> =>
  api.getBlob(`${BASE}/files/${fileId}/download`);

export const getJohnDeereFields = (farmUri: string): Promise<{ fields: JohnDeereField[] }> =>
  api.get<{ fields: JohnDeereField[] }>(`${BASE}/fields`, { farmUri });

/**
 * Get fields directly by org (bypasses farms — works even when farms returns 403).
 * Pass clientId to return only that client's (grower's) fields.
 */
export const getJohnDeereFieldsByOrg = (
  orgUri: string,
  clientId?: string
): Promise<{ fields: JohnDeereField[] }> =>
  api.get<{ fields: JohnDeereField[] }>(
    `${BASE}/fields`,
    clientId ? { orgUri, clientId } : { orgUri }
  );

export interface JohnDeereImportRequest {
  orgUri: string;
  farmUri?: string; // optional — import works without farms
  fieldUris: string[];
  clientId?: string; // John Deere Client (grower) the fields belong to
  clientName?: string;
  farmId?: string; // GreenGrowth farm to file the imported fields under
}

export const importJohnDeereFields = (
  body: JohnDeereImportRequest
): Promise<JohnDeereImportResult> =>
  api.post<JohnDeereImportResult>(`${BASE}/import`, body);

/** A machine in the "Import from John Deere" device picker (feature config `john_deere_combines`). */
export interface JohnDeereMachine {
  equipmentId: string;
  principalId: string | null;
  name: string;
  make: string | null;
  model: string | null;
  vin: string | null;
  category: string | null;
  /** Deere category / model says combine. The picker shows only these unless "show all". */
  combine: boolean;
  /** Already a device on this account. */
  imported: boolean;
  /** Imported by another GreenGrowth account — cannot be imported here. */
  importedElsewhere: boolean;
}

/**
 * Why one machine was not imported. `code` is what the picker shows (localised);
 * `error` is the backend's English text, used only when no code came back.
 */
export type JohnDeereMachineImportError = {
  equipmentId: string;
  error: string;
  /** MACHINE_NOT_FOUND | IMPORTED_ELSEWHERE | IMPORT_FAILED */
  code?: string;
};

export interface JohnDeereMachinesImportResult {
  imported: Array<{ esn: string; name: string }>;
  errors: JohnDeereMachineImportError[];
}

/** Most ids POST /machines/import accepts in one call (route schema `.max(50)`). */
export const JD_MACHINE_IMPORT_BATCH = 50;

/** `silent`: the picker renders John Deere errors itself (terms / connections links). */
export const getJohnDeereMachines = (
  orgUri: string,
  showAll: boolean
): Promise<{ machines: JohnDeereMachine[] }> =>
  api.get<{ machines: JohnDeereMachine[] }>(
    `${BASE}/machines`,
    { orgUri, all: showAll ? "1" : "0" },
    { silent: true }
  );

/**
 * Sent in batches of JD_MACHINE_IMPORT_BATCH: one call over the route's limit
 * is a 400 and imports nothing. A failure on the first batch rejects as usual
 * (the picker renders the connections link); a later one keeps what the earlier
 * batches imported and reports the rest as failed.
 */
export const importJohnDeereMachines = async (
  orgUri: string,
  equipmentIds: string[]
): Promise<JohnDeereMachinesImportResult> => {
  const result: JohnDeereMachinesImportResult = { imported: [], errors: [] };
  for (let i = 0; i < equipmentIds.length; i += JD_MACHINE_IMPORT_BATCH) {
    const batch = equipmentIds.slice(i, i + JD_MACHINE_IMPORT_BATCH);
    try {
      const r = await api.post<JohnDeereMachinesImportResult>(
        `${BASE}/machines/import`,
        { orgUri, equipmentIds: batch },
        { silent: true }
      );
      result.imported.push(...(r.imported ?? []));
      result.errors.push(...(r.errors ?? []));
    } catch (e: any) {
      if (i === 0) throw e;
      equipmentIds.slice(i).forEach((equipmentId) =>
        result.errors.push({ equipmentId, error: e?.message ?? "Import failed", code: "IMPORT_FAILED" })
      );
      break;
    }
  }
  return result;
};

export interface JohnDeerePrescriptionUploadRequest {
  fieldId: string;
  seasonId: string;
  zones: number;
  rates: number[];
  deereOrgId: string;
  deereFieldId: string;
  fieldName?: string;
}

export const uploadJohnDeerePrescription = (
  body: JohnDeerePrescriptionUploadRequest
): Promise<JohnDeerePrescriptionUploadResult> =>
  api.post<JohnDeerePrescriptionUploadResult>(`${BASE}/prescription/upload`, body);

export interface JohnDeereSyncSettings {
  /** When true, yields auto-sync to Operations Center daily + on mark-complete. Opt-in: OFF after a new connection. */
  autoSyncYields: boolean;
  /** Weight unit used for the yield maps pushed to Operations Center (WeightUnit value, e.g. "t", "bag"). Account-scoped. */
  weight: string;
  /** Area unit used for the yield maps pushed to Operations Center (AreaUnit value, e.g. "ha", "ac"). Account-scoped. */
  area: string;
  /** Pounds per bag when weight === "bag" (null → server default 100). */
  poundsPerBag?: number | null;
  /** Pounds per bushel when weight === "bu" (null → server default 60). */
  poundsPerBushel?: number | null;
}

export const getJohnDeereSyncSettings = (): Promise<JohnDeereSyncSettings> =>
  api.get<JohnDeereSyncSettings>(`${BASE}/settings`);

export const putJohnDeereSyncSettings = (
  body: JohnDeereSyncSettings
): Promise<JohnDeereSyncSettings> =>
  api.put<JohnDeereSyncSettings>(`${BASE}/settings`, body);
